/* eslint-disable no-param-reassign */
import { MapLayerMgr } from './layer-management';
import {
  LAYERS,
  MAPBOX_ACCESS_TOKEN,
  MAPBOX_DEFAULT_STYLE,
  MAPBOX_SATELLITE_STYLE,
  MAP_CONTAINER_ID,
  SOURCES,
  mapStyles,
} from './map-constants';
import { MapSourceMgr } from './map-source-mgmt';

/**
 * This decorator will ensure the map has been loaded before executing a function.
 * If the map hasn't been loaded yet, the function call is added to a queue and will
 * be executed once the 'load' event is trigged on the map.
 * It was designed for using on MapInstanceMgr methods
 *
 * @param {*} target the class of the method
 * @param {*} name name of method
 * @param {*} descriptor descriptor object for method
 */
function executeOnLoaded(target, name, descriptor) {
  // decorated method
  const fn = descriptor.value;
  // overrides decorated method with function
  descriptor.value = (...args) => {
    // getting mapInstance manager instance because the `this` context was lost
    // eslint-disable-next-line no-use-before-define
    const mapInstanceMgr = MapboxInstanceMgr.getInstance();
    const { mapInstance } = mapInstanceMgr;
    if (MapboxInstanceMgr.isReady) {
      // eslint-disable-line no-use-before-define
      fn.apply(mapInstanceMgr, args);
    } else {
      mapInstance.on('load', () => {
        fn.apply(mapInstanceMgr, args);
      });
    }
  };
  return descriptor;
}

/**
 * This is a singleton class, meant to manage all mapboxgl instance's functionality.
 */
export class MapboxInstanceMgr {
  static instance = null;

  static isReady = false;

  static DEFAULT_MAP_STYLE = mapStyles[MAPBOX_DEFAULT_STYLE];

  static STARTING_ZOOM = 15;

  static hasBeenInitialized() {
    return Boolean(MapboxInstanceMgr.instance);
  }

  static getInstance() {
    if (!MapboxInstanceMgr.instance) {
      MapboxInstanceMgr.instance = new MapboxInstanceMgr();
    }
    return MapboxInstanceMgr.instance;
  }

  static verifyStylesHaveLoaded() {
    if (!this.hasBeenInitialized()) return false;

    const { mapInstance } = this.getInstance();
    return mapInstance.isStyleLoaded();
  }

  constructor() {
    if (MapboxInstanceMgr.instance) {
      throw new Error(
        'An instance of MapboxMgr already exists. This class is a singleton'
      );
    }
    MapboxInstanceMgr.instance = this;
  }

  // Placeholder for mapbox Map instance
  mapInstance = null;

  firstSymbolId = null;

  addCustomControlMountsCB = null;

  mapSourceMgr = null;

  onDotClick = null; // for setting featured property (PulsingDot), or go to PropertyDetails

  getMapboxglStyleName() {
    return this.mapInstance.getStyle().name;
  }

  init = ({
    cityInfo,
    onDotClick,
    addCustomControlMountsCB,
    platformIcons,
    displayUnlicensedPropertiesSeparately,
    disableClustering,
  }) => {
    if (this.mapInstance) return;
    this.addCustomControlMountsCB = addCustomControlMountsCB;
    this.onDotClick = onDotClick;
    this.initializeMapboxglInstance(cityInfo);
    this.mapSourceMgr = new MapSourceMgr(this.mapInstance);
    this.mapLayerMgr = new MapLayerMgr(this.mapInstance);
    this.platformIcons = platformIcons;
    this.displayUnlicensedPropertiesSeparately =
      displayUnlicensedPropertiesSeparately;
    this.disableClustering = disableClustering;
  };

  initializeMapboxglInstance(cityInfo) {
    this.mapInstance = new window.mapboxgl.Map({
      accessToken: MAPBOX_ACCESS_TOKEN,
      container: MAP_CONTAINER_ID,
      style: MapboxInstanceMgr.DEFAULT_MAP_STYLE,
      zoom: MapboxInstanceMgr.STARTING_ZOOM, // starting zoom, overriden by bounds if it's not null
      bounds: [cityInfo.max_lnglat, cityInfo.min_lnglat],
      pitchWithRotate: false, // disable all rotate
      dragRotate: false, // disable all rotate
      touchZoomRotate: false, // disable all rotate
      fadeDuration: 0,
      attributionControl: true,
      customAttribution: 'Rentalscape powered by ',
      preserveDrawingBuffer: true, // Required to export as png
    });

    // this must run before methods that draw in the map. E.g.: `.drawProperties`
    this.registerDefaultMapOnLoadCB();
  }

  registerDefaultMapOnLoadCB() {
    this.mapInstance.once('load', () => {
      MapboxInstanceMgr.isReady = true;

      // Finds and records first thing drawn in the map. Allows us to determine the order
      // in which our layers are drawn (E.g.: Zoning should not be on top of City name)
      this.findFirstSimbolId(); // first default map symbol

      this.mapInstance.addControl(
        new window.mapboxgl.NavigationControl(),
        'bottom-right'
      );
      // divs that serve as mounts for custom controls to be portaled into
      // this is here to ensure parent divs for portal already in dom before mounting portaled ctrls
      this.addCustomControlMountsCB();
    });
  }

  /**
   * Finds id of first "Symbol". Namely, finds the layerId of the first thing to be drawn
   * inside the map by mapbox. This is useful for inserting layers before anything else
   * is drawn
   */
  findFirstSimbolId() {
    const { layers } = this.mapInstance.getStyle(); // default layers
    // Find the index of the first symbol layer in the map style
    let firstSymbolId;
    for (let i = 0; i < layers.length; i++) {
      if (layers[i].type === 'symbol') {
        firstSymbolId = layers[i].id;
        break;
      }
    }
    this.firstSymbolId = firstSymbolId;
  }

  @executeOnLoaded
  drawProperties(propertyListGeojson) {
    this.propertyListGeojson = propertyListGeojson;
    this.mapSourceMgr.addPropertyListSource(propertyListGeojson, {
      disableClustering: this.disableClustering,
    });
    this.mapLayerMgr.addPropertyListLayer(
      this.firstSymbolId,
      this.onDotClick,
      this.platformIcons,
      this.displayUnlicensedPropertiesSeparately,
      this.disableClustering ? { minLayerZoom: 0 } : undefined
    );
    this.mapLayerMgr.addPropertyClusterLayer();
  }

  @executeOnLoaded
  drawBoundary(shapeFile) {
    this.shapeFile = shapeFile;
    this.mapSourceMgr.addShapeFileSource(shapeFile);
    this.mapLayerMgr.addBoundaryLayer(shapeFile, this.firstSymbolId);
  }

  @executeOnLoaded
  drawZoning(zoningFiles) {
    this.zoningFiles = zoningFiles;
    this.mapSourceMgr.addZoningSource(zoningFiles);
    this.mapLayerMgr.addZoningLayer(zoningFiles, LAYERS.PROPERTY_LIST);
  }

  @executeOnLoaded
  drawFilteredZoning(filteredZones) {
    this.mapLayerMgr.changeZonesFeatureColor(filteredZones);
  }

  resetFilteredZoneColor() {
    this.mapLayerMgr.resetZoneFeatureColor();
  }

  updatePropertyData = (propertyListGeojson) => {
    this.propertyListGeojson = propertyListGeojson;
    if (this.mapInstance?.getSource(SOURCES.PROPERTY_LIST)) {
      this.mapInstance
        .getSource(SOURCES.PROPERTY_LIST)
        .setData(propertyListGeojson);
    }
  };

  /**
   * Will destroy the mapInstance, and reset everything
   */
  reset = () => {
    if (this.mapInstance) {
      this.mapInstance.remove();
    }
    MapboxInstanceMgr.isReady = false;
    MapboxInstanceMgr.instance = null;
  };

  /**
   * Exists to redraw on map after style change
   * @param {*} param0
   */
  registerRedrawFeaturesCB = () => {
    // run once to avoid callbacks accumulating
    this.mapInstance.once('styledata', () => {
      this.findFirstSimbolId();
      if (this.propertyListGeojson)
        this.drawProperties(this.propertyListGeojson);
      if (this.shapeFile) this.drawBoundary(this.shapeFile);
      if (this.zoningFiles) this.drawZoning(this.zoningFiles);
    });
  };

  toggleStyle = () => {
    const mapboxInstance = MapboxInstanceMgr.getInstance().mapInstance;
    this.registerRedrawFeaturesCB();
    if (mapboxInstance.getStyle().name === MAPBOX_DEFAULT_STYLE) {
      mapboxInstance.setStyle(mapStyles[MAPBOX_SATELLITE_STYLE]);
    } else {
      mapboxInstance.setStyle(mapStyles[MAPBOX_DEFAULT_STYLE]);
    }
  };

  getStyleName = () => this.mapInstance.getStyle().name;
}

// Exposed so we can test the map with Cypress
window.__MapboxInstanceMgr = MapboxInstanceMgr;
